import React from "react";
import { Dialog as BaseDialog } from "Components/Base/Feedback/Dialog";
import { DialogActions } from "Components/Base/Feedback/DialogActions";
import { DialogTitle } from "Components/Base/Feedback/DialogTitle";
import { DialogContent } from "Components/Base/Feedback/DialogContent";
import { Button } from "Components/Base/Inputs/Button";
import { IconButton } from "Components/Base/Inputs/IconButton";
import { Clear } from "Components/Icons";
import { Paper } from "Components/Base/Surfaces/Paper";
import Draggable from "react-draggable";
import useScrollBox from "Components/Hooks/useScrollBox";
import { LinearProgress } from "Components/Base/Feedback/LinearProgress";
import BaseGrid from "Components/Fragment/BaseGrid";
import { Box } from "Components/Base/Layout/Box";
// eslint-disable-next-line import/no-relative-packages
import useMobileViewportCheck from "../../../Portal/web/Content/js/lib/effects/useMobileViewportCheck";

/**
 * Wraps the content of the dialog in a draggable Paper component.
 * @param {Object} props The props passed to the Paper component.
 * @returns {React.Component} A draggable version of the Paper component.
 */
const PaperComponent = (props) => {
  return (
    <Draggable
      handle="#dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} aria-label={props.title || "dialog-paper"} />
    </Draggable>
  );
};

/**
 * A dialog component with optional close & confirm buttons and draggable feature.
 *
 * @param {Function} [props.onClose=() => {}} - The function to be called when the dialog is closed. MANDATORY unless showCloseButton is set to false.
 * @param {string|null} [props.confirmText=null] - The text for the confirm button. If null, no confirm button is rendered.
 * @param {Function} props.handleConfirm - The function to call when the confirm button is clicked.
 * @param {string|null} [props.cancelText=null] - The text for the cancel button. If null, no cancel button is rendered.
 * @param {Function} [props.handleCancel] - Optional. The function to call when the cancel button is clicked. If null, defaults to onClose.
 * @param {boolean} [props.disableConfirm=false] - Controls whether the confirm button is disabled.
 * @param {string} props.title - The title of the dialog.
 * @param {string} props.text - The content of the dialog.
 * @param {boolean} props.open - Controls the visibility of the dialog.
 * @param {string} [props.maxWidth="xs"] - The maximum width of the dialog. Defaults to "xs".
 * @param {boolean} [props.draggable=true] - Enables or disables the draggable feature. Defaults to true.
 * @param {boolean} [props.scrollable=false] - Enables or disables the scrollable feature. Defaults to false.
 * @param {boolean} props.fullWidth - Determines if the dialog should be full-width.
 * @param {string} props.severity - The severity of the dialog. This could be used for styling purposes.
 * @param {boolean} [props.disableEnforceFocus] - Controls whether the focus is enforced or not.
 * @param {string} [props.cancelButtonVariant] - The variant of the cancel button.
 * @param {string} [props.closeTitle] - The title attribute for the close button, used for accessibility.
 * @param {boolean} [props.showCloseButton=true] - Determines whether the close button is displayed.
 * @param {React.ReactNode} [props.cancelButton] - Custom cancel button component
 * @param {React.ReactNode} [props.confirmButton] - Custom confirm button component
 * @param {boolean} [props.isLoading=false] - Controls whether to show a loading indicator in the dialog content. When true, displays a LinearProgress component above the content.
 * @returns {React.Component} A dialog component with optional cancel & confirm buttons.
 */
const Dialog = ({
  onClose = () => {},
  confirmText = null,
  handleConfirm,
  cancelText = null,
  handleCancel,
  disableConfirm = false,
  title,
  text,
  open,
  maxWidth = "xs",
  draggable = true,
  scrollable = false,
  fullWidth,
  disableEnforceFocus,
  closeTitle,
  showCloseButton = true,
  cancelButton,
  confirmButton,
  isLoading = false,
}) => {
  const mobileViewport = useMobileViewportCheck();
  const isDraggable = draggable && !mobileViewport;
  const ariaTitle = "dialog-title";
  const ariaContent = "dialog-content";

  const titleContainerStyles = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  };

  const renderContent = () => {
    return (
      <>
        <Box sx={titleContainerStyles}>
          <DialogTitle
            id={ariaTitle}
            style={isDraggable ? { cursor: "move" } : undefined}
          >
            {title}
          </DialogTitle>
          {showCloseButton && (
            <IconButton
              title={closeTitle || cancelText}
              onClick={onClose}
              icon={<Clear />}
            />
          )}
        </Box>
        <DialogContent id={ariaContent}>
          {isLoading ? (
            <BaseGrid>
              <LinearProgress color="primary" variant="indeterminate" />
              {text}
            </BaseGrid>
          ) : (
            text
          )}
        </DialogContent>
        <DialogActions>
          {cancelButton ||
            (cancelText && (
              <Button
                onClick={handleCancel || onClose}
                color={confirmText || confirmButton ? "secondary" : "primary"}
                variant={
                  confirmText || confirmButton ? "outlined" : "contained"
                }
                label={cancelText}
              />
            ))}
          {confirmButton ||
            (confirmText && (
              <Button
                onClick={handleConfirm}
                label={confirmText}
                disabled={isLoading || disableConfirm}
              />
            ))}
        </DialogActions>
      </>
    );
  };

  return (
    <BaseDialog
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      fullScreen={mobileViewport}
      aria-labelledby={ariaTitle}
      aria-describedby={ariaContent}
      {...(isDraggable ? { PaperComponent } : {})}
      open={!!open}
      disableEnforceFocus={disableEnforceFocus}
      onClose={onClose}
    >
      {scrollable ? useScrollBox(renderContent()) : renderContent()}
    </BaseDialog>
  );
};

export default Dialog;
